// Native
import { useNavigate } from "react-router"
import { Badge, Box, Button, Flex, Grid, GridItem, HStack, Separator, Stack, Table, Text } from "@chakra-ui/react"
import { AreaChart, Area, Tooltip, ResponsiveContainer, CartesianGrid, XAxis } from "recharts"

// Custom
import { UI } from "components"
import useSalesReport from "hooks/useSalesReport"
import moment from "moment"
import { Mask, Styles } from "config"
import useOrders from "hooks/useOrders"
import useReportSalesGeneral from "hooks/useReportSalesGeneral"
import useStockReport from "hooks/useStockReport"

export default function Home(){

    const navigate = useNavigate()
    const sales_report = useSalesReport()
    const orders = useOrders(1, null, null, 12)
    const sales = useReportSalesGeneral()
    const stock = useStockReport()

    let last_orders = orders.data?.data ? [...orders.data?.data] : []
    last_orders = last_orders?.slice(0, 5)

    const data = sales_report.data?.map(item => ({ date: item.date, daily_growth: item.daily_growth }))

    return(
    <>
    <UI.Header title="Visão Geral" />

    {/* ── KPI Stat cards ── */}
    <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <StatCard label="Em análise"       value={sales.data?.analyzing} icon="analyzing" color="yellow" loading={sales.isLoading} />
        <StatCard label="Aprovados"        value={sales.data?.approved}  icon="check"     color="green"  loading={sales.isLoading} />
        <StatCard label="Em trânsito"      value={sales.data?.transit}   icon="transit"   color="blue"   loading={sales.isLoading} />
        <StatCard label="Aguard. retirada" value={sales.data?.withdraw}  icon="withdraw"  color="teal"   loading={sales.isLoading} />
    </Grid>

    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>

        {/* ── Gráfico de vendas ── */}
        <GridItem colSpan={{ base: 1, md: 2, lg: 2 }}>
            <UI.Box loading={sales_report.isLoading} p={6} bg="bg.subtle" borderRadius={12} shadow="xs" h="100%">
                <HStack justify="space-between" mb={5}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold">Vendas — últimos 30 dias</Text>
                        <Text fontSize="xs" color="fg.muted" mt={0.5}>Crescimento diário acumulado</Text>
                    </Box>
                    <Box p={2.5} borderRadius={8} bg="teal.muted">
                        <UI.Icon name="reports" color="teal.500" boxSize={4} />
                    </Box>
                </HStack>

                <ResponsiveContainer width="100%" height={185}>
                    <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <defs>
                            <linearGradient id="gradSales" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#38B2AC" stopOpacity={0.22} />
                                <stop offset="95%" stopColor="#38B2AC" stopOpacity={0}    />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.1)" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tickFormatter={v => moment(v).format('DD/MM')}
                            tick={{ fontSize: 11, fill: 'rgba(128,128,128,0.7)' }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Area
                            type="monotone"
                            dataKey="daily_growth"
                            stroke="#38B2AC"
                            strokeWidth={2.5}
                            fill="url(#gradSales)"
                            dot={false}
                            activeDot={{ r: 5, fill: '#38B2AC', stroke: 'white', strokeWidth: 2 }}
                        />
                        <Tooltip
                            labelFormatter={() => 'Total de vendas'}
                            formatter={v => [`R$ ${Mask.price(v)}`, 'Vendas']}
                            contentStyle={{ backgroundColor: 'rgba(15,23,42,0.92)', border: 0, borderRadius: '10px', fontSize: 12, padding: '8px 14px' }}
                            cursor={{ stroke: 'rgba(128,128,128,0.18)', strokeWidth: 1 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>

                <HStack justify="flex-end" mt={3}>
                    <Button size="xs" variant="ghost" colorPalette="teal" onClick={() => navigate('/relatorio/geral')}>
                        Ver relatório completo <UI.Icon name="right" />
                    </Button>
                </HStack>
            </UI.Box>
        </GridItem>

        {/* ── Estoque ── */}
        <GridItem>
            <UI.Box loading={stock.isLoading} p={6} borderRadius={12} shadow="xs" h="100%">
                <HStack justify="space-between" mb={5}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold">Estoque</Text>
                        <Text fontSize="xs" color="fg.muted" mt={0.5}>Posição atual</Text>
                    </Box>
                    <Box p={2.5} borderRadius={8} bg="orange.muted">
                        <UI.Icon name="inventory" color="orange.500" boxSize={4} />
                    </Box>
                </HStack>

                <Stack gap={2}>
                    <StockRow label="No estoque" value={stock.data?.stock}    color="green" />
                    <StockRow label="Reservados"  value={stock.data?.reserved} color="blue"  />
                    <StockRow label="Em falta"    value={stock.data?.empty}    color="red"   />
                </Stack>

                <Separator my={4} />

                <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={2}>Vouchers</Text>
                <HStack justify="space-between" p={3} borderRadius={8} bg="purple.muted">
                    <Text fontSize="xs" color="fg.muted">Plano Start Turbo</Text>
                    <Badge colorPalette="purple" size="sm" borderRadius="full">{stock.data?.vouchers ?? '—'}</Badge>
                </HStack>

                <Button w="100%" mt={5} size="sm" variant="ghost" onClick={() => navigate('/estoque')}>
                    Ver estoque completo <UI.Icon name="right" />
                </Button>
            </UI.Box>
        </GridItem>

        {/* ── Últimas vendas ── */}
        <GridItem colSpan={{ base: 1, md: 2, lg: 2 }}>
            <UI.Box p={6} bg="bg.subtle" borderRadius={12} shadow="xs" h="100%">
                <HStack justify="space-between" mb={5}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold">Últimas vendas</Text>
                        <Text fontSize="xs" color="fg.muted" mt={0.5}>5 pedidos mais recentes</Text>
                    </Box>
                    <Box p={2.5} borderRadius={8} bg="orange.muted">
                        <UI.Icon name="orders" color="orange.500" boxSize={4} />
                    </Box>
                </HStack>

                <UI.Table columns={[{ children: 'Pedido' }, { children: '' }]} data={last_orders} Row={Row} />

                <Button w="100%" mt={5} size="sm" variant="outline" onClick={() => navigate('/pedidos')}>
                    Ver todas as vendas <UI.Icon name="right" />
                </Button>
            </UI.Box>
        </GridItem>

        {/* ── Acesso rápido ── */}
        <GridItem>
            <UI.Box p={6} borderRadius={12} shadow="xs" h="100%">
                <Text fontSize="sm" fontWeight="semibold" mb={0.5}>Acesso rápido</Text>
                <Text fontSize="xs" color="fg.muted" mb={5}>Atalhos principais</Text>

                <Grid templateColumns="repeat(2, 1fr)" gap={2.5}>
                    {quickActions.map(action => (
                        <QuickAction key={action.path} {...action} />
                    ))}
                </Grid>
            </UI.Box>
        </GridItem>

    </Grid>
    </>
    )
}

const StatCard = ({ label, value, icon, color, loading }) => (
    <Box
        px={5}
        pt={6}
        pb={5}
        borderRadius={12}
        bg="bg.subtle"
        borderWidth={1}
        position="relative"
        overflow="hidden"
        shadow="xs"
        transition="box-shadow 0.2s"
        _hover={{ shadow: 'sm' }}
    >
        {/* Accent strip */}
        <Box position="absolute" top={0} left={0} right={0} h="3px" bg={`${color}.400`} />

        <HStack justify="space-between" align="flex-start">
            <Box>
                <Text
                    fontSize="2xs"
                    color="fg.muted"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="widest"
                    mb={2.5}
                >
                    {label}
                </Text>
                <Text
                    fontSize="4xl"
                    fontWeight="extrabold"
                    lineHeight="1"
                    letterSpacing="-0.03em"
                    color={loading ? 'transparent' : 'fg'}
                >
                    {loading ? '00' : (value ?? '—')}
                </Text>
                <Text fontSize="2xs" color="fg.subtle" mt={1.5} textTransform="uppercase" letterSpacing="wider">
                    pedidos
                </Text>
            </Box>
            <Box p={2.5} borderRadius={8} bg={`${color}.muted`} flexShrink={0} mt={0.5}>
                <UI.Icon name={icon} boxSize={4} color={`${color}.500`} />
            </Box>
        </HStack>
    </Box>
)

const StockRow = ({ label, value, color }) => (
    <HStack justify="space-between" py={2.5} px={3} borderRadius={8} bg={`${color}.muted`}>
        <HStack gap={2}>
            <Box w="7px" h="7px" borderRadius="full" bg={`${color}.500`} flexShrink={0} />
            <Text fontSize="xs" color="fg.muted">{label}</Text>
        </HStack>
        <Text fontSize="sm" fontWeight="bold" color={`${color}.500`}>{value ?? '—'}</Text>
    </HStack>
)

const quickActions = [
    { title: 'Vendas',      icon: 'orders',    path: '/pedidos',         color: 'orange' },
    { title: 'Estoque',     icon: 'inventory', path: '/estoque',         color: 'teal'   },
    { title: 'Compras',     icon: 'cart',      path: '/compras',         color: 'blue'   },
    { title: 'Financeiro',  icon: 'withdraw',  path: '/financeiro',      color: 'green'  },
    { title: 'Relatórios',  icon: 'reports',   path: '/relatorio/geral', color: 'purple' },
    { title: 'Loja',        icon: 'store',     path: '/loja',            color: 'pink'   },
]

const QuickAction = ({ title, icon, path, color }) => {
    const navigate = useNavigate()
    return (
        <Box
            py={4}
            px={2}
            borderRadius={10}
            borderWidth={1}
            cursor="pointer"
            textAlign="center"
            transition="all 0.15s ease"
            _hover={{
                bg: `${color}.muted`,
                borderColor: `${color}.300`,
                transform: 'translateY(-2px)',
                shadow: 'sm',
            }}
            onClick={() => navigate(path)}
        >
            <Flex direction="column" align="center" gap={2}>
                <Box p={2} borderRadius={8} bg={`${color}.muted`}>
                    <UI.Icon name={icon} color={`${color}.500`} boxSize={4} />
                </Box>
                <Text fontSize="2xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
                    {title}
                </Text>
            </Flex>
        </Box>
    )
}

const Row = ({ data }) => {

    const navigate = useNavigate()
    const style = Styles.orderStatus(data.status)

    return(
    <Table.Row cursor="pointer" onClick={() => navigate(`/pedidos/${data.id}`)} _hover={{ bg: 'bg.emphasized' }}>
        <Table.Cell fontSize="sm" fontWeight="medium">{data.cod}</Table.Cell>
        <Table.Cell>
            <HStack justify="flex-end">
                <Badge size="sm" colorPalette={style.colorPalette} borderRadius="full">
                    <UI.Icon name={style.icon} /> {style.label}
                </Badge>
                <UI.Tag.Avatar name={data.consultant?.customer?.name} src={data.consultant?.customer?.avatar_url} />
                <Text fontSize="xs" color="fg.muted">{moment(data.created_at).fromNow()}</Text>
            </HStack>
        </Table.Cell>
    </Table.Row>
    )
}
